import { COMMON_CLASS_NAMES } from '../constants';
import { DomAnimation } from '../utils/domAnimation';

/**
 * The `Dropdown` class provides functionality for creating and managing dropdown components.
 * It supports animations for opening and closing, accessibility features, and media query-based
 * availability conditions. The class is designed to work with both `<details>` elements and
 * custom containers with specific class names.
 *
 * @class
 */
export class Dropdown {
  /** @type {Element} */
  container;
  /** @type {Set<HTMLElement>} */
  triggers = new Set();
  /** @type {Set<HTMLElement>} */
  contents = new Set();
  /**
   * An object representing the media query breakpoints for a dropdown.
   * @property {number|undefined} max - The maximum width for the media query. Undefined if not set.
   * @property {number|undefined} min - The minimum width for the media query. Undefined if not set.
   */
  dropdownMediaQuery = {
    max: undefined,
    min: undefined
  }

  /**
   * Creates an instance of the Dropdown class.
   *
   * @constructor
   * @param {HTMLElement} el - The container element for the dropdown.
   *
   * @property {HTMLElement} container - The container element for the dropdown.
   * @property {HTMLCollection|NodeList} triggers - The trigger elements for the dropdown.
   *   If the container is an HTMLDetailsElement, it retrieves <summary> elements;
   *   otherwise, it retrieves elements with the class name defined in Dropdown.CLASS_NAMES.TRIGGER.
   * @property {Set<HTMLElement>} contents - A set of elements controlled by the triggers,
   *   identified by their `aria-controls` attribute.
   * @property {Object} dropdownMediaQuery - An object containing media query constraints for the dropdown.
   * @property {string} dropdownMediaQuery.max - The maximum media query constraint, retrieved from the container's `data-dropdown-media-query-max` attribute.
   * @property {string} dropdownMediaQuery.min - The minimum media query constraint, retrieved from the container's `data-dropdown-media-query-min` attribute.
   *
   * @throws {Error} Will throw an error if an element referenced by `aria-controls` is not found in the DOM.
   */
  constructor(el) {
    this.container = el;

    /** get the triggers */
    const summaries = Array.from(this.container.children).filter(child => child.tagName === "SUMMARY");
    for (let i = 0; i < summaries.length; i++) {
      const el = summaries[i];
      this.triggers.add(el);
    }

    const triggers = this.container.getElementsByClassName(Dropdown.CLASS_NAMES.TRIGGER);
    for (let i = 0; i < triggers.length; i++) {
      const trigger = triggers[i];
      this.triggers.add(trigger);

      const ariaControls = trigger.getAttribute('aria-controls');
      if (ariaControls) {
        ariaControls.split(/\s/).forEach((id) => {
          const el = document.getElementById(id);
          el && !(el instanceof HTMLDetailsElement) && this.contents.add(el);
        });
      }
    }

    const contents = this.container.getElementsByClassName(Dropdown.CLASS_NAMES.CONTENTS);
    for (let k = 0; k < contents.length; k++) {
      const content = contents[k];
      this.contents.add(content);
    }

    /** get the media query breakpoints */
    this.dropdownMediaQuery = {
      max: this.container.dataset.dropdownMediaQueryMax,
      min: this.container.dataset.dropdownMediaQueryMin
    };

    this.listen();
    this.container.classList.add(COMMON_CLASS_NAMES.INIT);

  }

  static CLASS_NAMES = {
    CONTAINER: 'js-dropdown',
    TRIGGER: 'js-dropdown-trigger',
    CONTENTS: 'js-dropdown-contents'
  }

  /**
   * @method listen
   * Initializes event listeners for all trigger elements.
   * Iterates through the `triggers` array and attaches a click event listener
   * to each trigger, binding the `handleClick` method to the current instance.
   */
  listen() {
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', this.handleClick.bind(this), { passive: false });
    })

    this.handleResize();
    if (Object.values(this.dropdownMediaQuery).some(val => val != null)) {
      window.addEventListener('resize', this.handleResize.bind(this));
    }
  }

  /**
   * @method handleResize
   * Handles the resize event and updates the container's class
   * based on the availability status.
   *
   */
  handleResize() {
    if (this.isAvailable()) {
      this.container.classList.add(COMMON_CLASS_NAMES.AVAILABLE);
    } else {
      this.container.classList.remove(COMMON_CLASS_NAMES.AVAILABLE);
    }
  }

  /**
   * @method isOpen
   * Checks if the container is currently open.
   *
   * @returns {boolean} Returns `true` if the container is open, either by the `open` property
   *                    or by having the `is-active` class; otherwise, returns `false`.
   */
  isOpen() {
    return this.container.open || this.container.classList.contains(COMMON_CLASS_NAMES.ACTIVE);
  }

  /**
   * Determines if the current window width falls within the specified media query range.
   *
   * @returns {boolean} - Returns `true` if the window width is within the range defined
   *                      by `dropdownMediaQuery.min` and `dropdownMediaQuery.max`, otherwise `false`.
   */
  isAvailable() {
    /** @type {boolean} */
    let result = true;
    if (this.dropdownMediaQuery.max != null) {
      result = result && window.innerWidth < this.dropdownMediaQuery.max;
    }
    if (this.dropdownMediaQuery.min != null) {
      result = result && window.innerWidth > this.dropdownMediaQuery.min;
    }
    return result;
  }

  /**
   * Handles the click event for the associated element.
   * Prevents the default action and toggles the state of the component
   * if it is available and not currently in a "running" transition state.
   *
   * @param {Event} e - The click event object.
   */
  handleClick(e) {
    e.stopPropagation();
    if (this.transitionState === "running" || !this.isAvailable()) return;
    e.preventDefault();
    this.toggle(!this.isOpen());
  }

  /**
   * @method toggle
   * Toggles the open or closed state of a container element with animations.
   *
   * @param {boolean} open - A boolean indicating whether to open (`true`) or close (`false`) the container.
   *
   * @description
   * This method handles the toggling of a container's state, including animations for opening and closing.
   * It updates the `aria-hidden` attribute for accessibility, manages the `open` attribute for
   * `<details>` elements, and toggles the `ACTIVE` class for other container types. The method also
   * ensures that the `aria-expanded` attribute of associated triggers is updated accordingly.
   *
   * The animations are defined by `this.keyframes.open` and `this.keyframes.close`, and their options
   * are specified in `this.keyframesOptions`. The method waits for all animations to complete before
   * finalizing the state of the container.
   */
  toggle(open) {
    this.transitionState = "running";
    const animation = new DomAnimation();
    const animations = [];
    if (open) {
      this.container instanceof HTMLDetailsElement && this.container.toggleAttribute("open", true);
      this.container.classList.add(COMMON_CLASS_NAMES.ACTIVE);
      this.contents.forEach(content => {
        const animPromiseOpen = new Promise(resolve => {
          animation.animate(content, DomAnimation.ANIMATION_TYPE.SLIDE_IN, {duration: 800}, () => {
            content.hasAttribute('aria-hidden') && content.setAttribute('aria-hidden', (!open).toString());
            return resolve();
          });
        });
        animations.push(animPromiseOpen);
      });
    } else {
      this.contents.forEach(content => {
        const animPromiseClose = new Promise(resolve => {
          animation.animate(content, DomAnimation.ANIMATION_TYPE.SLIDE_OUT, {}, () => {
            content.hasAttribute('aria-hidden') && content.setAttribute('aria-hidden', (!open).toString());
            return resolve();
          });
        });
        animations.push(animPromiseClose);
      });

      Promise.all(animations).then(() => {
        if (this.container instanceof HTMLDetailsElement) {
          this.container.toggleAttribute("open", open);
        }
        this.container.classList.remove(COMMON_CLASS_NAMES.ACTIVE);
      });
    }
    Promise.all(animations).finally(() => {
      this.triggers.forEach(trigger => {
        trigger.hasAttribute('aria-expanded') && trigger.setAttribute('aria-expanded', open.toString());
      })
      this.transitionState = "";
    });
  }


  open() {
    this.toggle(true);
  }

  close() {
    this.toggle(false);
  }

}
